import { ChatgptListingStatus } from "@redotech/crdb-enums/product/chatgpt-listing-status";
import { ChatgptProductFieldType } from "@redotech/crdb-enums/product/chatgpt-product-field-type";
import { ListingType } from "@redotech/crdb-enums/product/listing-type";
import { chatGptListingIdFormat } from "@redotech/redo-marketplace-id";
import { attributeDefinitionIdFormat } from "@redotech/redo-product-id";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  AttributeType,
  AttributeValueSchema,
  AttributeValueType,
  createSafeNullableEnumSchema,
  MeasurementSchema,
  ProductCategorySchema,
  WeightSchema,
} from "../attributes";
import { BaseProductFamilyListingSchema } from "../base-listing";
import { RedoListingStatus } from "../redo-listing-status";
import { ChatGptListingMediaSchema } from "./chatgpt-media";
import {
  ChatGptVariantListingSchema,
  ChatGptVariantMappingSchema,
} from "./chatgpt-variant";

export enum AgeGroup {
  Newborn = "newborn",
  Infant = "infant",
  Toddler = "toddler",
  Kids = "kids",
  Adult = "adult",
}
export const AgeGroupSchema = createEnum(AgeGroup);

export enum Condition {
  New = "new",
  Refurbished = "refurbished",
  Used = "used",
}
export const ConditionSchema = createEnum(Condition);

export const ChatGptListingStatusSchema = createEnum(ChatgptListingStatus);

export const ChatGptListingIdWithStatusSchema = z.object({
  id: chatGptListingIdFormat.schema,
  status: ChatGptListingStatusSchema,
});
export type ChatGptListingIdWithStatus = z.infer<
  typeof ChatGptListingIdWithStatusSchema
>;

export function convertChatGptListingStatusToRedoListingStatus(
  status: ChatgptListingStatus,
): RedoListingStatus {
  switch (status) {
    case ChatgptListingStatus.Active:
      return RedoListingStatus.ACTIVE;
    case ChatgptListingStatus.Draft:
      return RedoListingStatus.DRAFT;
    case ChatgptListingStatus.Archived:
      return RedoListingStatus.ARCHIVED;
    default:
      assertNever(status);
  }
}

export const ChatGptProductFieldTypeSchema = createEnum(
  ChatgptProductFieldType,
);

export const ChatGptListingSchema_ = BaseProductFamilyListingSchema.extend({
  listingType: z.literal(ListingType.Chatgpt),
  id: z.bigint(),
  gtin: z.string().nullable(),
  mpn: z.string().nullable(),
  title: z.string().nullable().default(""),
  description: z.string().nullable().default(""),
  link: z.string().nullable().default(""),
  productCategory: ProductCategorySchema.nullable(),
  brand: z.string().nullable().default(""),
  material: z.string().nullable().default(""),
  length: MeasurementSchema.nullable(),
  width: MeasurementSchema.nullable(),
  height: MeasurementSchema.nullable(),
  weight: WeightSchema.nullable(),
  ageGroup: AgeGroupSchema.nullable(),
  condition: ConditionSchema.nullable(),
  status: ChatGptListingStatusSchema,
  variants: z.array(ChatGptVariantListingSchema),
  media: z.array(ChatGptListingMediaSchema),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const ChatGptListingSchema: z.ZodType<ChatGptListing> =
  ChatGptListingSchema_;
export interface ChatGptListing extends z.infer<typeof ChatGptListingSchema_> {}

export const ChatGptProductAttributeType = {
  [ChatgptProductFieldType.Title]: AttributeType.ShortText,
  [ChatgptProductFieldType.Description]: AttributeType.LongText,
  [ChatgptProductFieldType.Link]: AttributeType.ShortText,
  [ChatgptProductFieldType.ProductCategory]: AttributeType.ProductCategory,
  [ChatgptProductFieldType.Condition]: AttributeType.Enum,
  [ChatgptProductFieldType.Brand]: AttributeType.ShortText,
  [ChatgptProductFieldType.Material]: AttributeType.ShortText,
  [ChatgptProductFieldType.Length]: AttributeType.Dimension,
  [ChatgptProductFieldType.Width]: AttributeType.Dimension,
  [ChatgptProductFieldType.Height]: AttributeType.Dimension,
  [ChatgptProductFieldType.Weight]: AttributeType.Weight,
  [ChatgptProductFieldType.AgeGroup]: AttributeType.Enum,
} satisfies Record<ChatgptProductFieldType, AttributeType>;

export const ChatGptProductAttributeSchema = {
  [ChatgptProductFieldType.Title]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatgptProductFieldType.Title]
    ].nullable(),
  [ChatgptProductFieldType.Description]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatgptProductFieldType.Description]
    ].nullable(),
  [ChatgptProductFieldType.Link]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatgptProductFieldType.Link]
    ].nullable(),
  [ChatgptProductFieldType.ProductCategory]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatgptProductFieldType.ProductCategory]
    ].nullable(),
  [ChatgptProductFieldType.Condition]:
    createSafeNullableEnumSchema(ConditionSchema),
  [ChatgptProductFieldType.Brand]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatgptProductFieldType.Brand]
    ].nullable(),
  [ChatgptProductFieldType.Material]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatgptProductFieldType.Material]
    ].nullable(),
  [ChatgptProductFieldType.Length]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatgptProductFieldType.Length]
    ].nullable(),
  [ChatgptProductFieldType.Width]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatgptProductFieldType.Width]
    ].nullable(),
  [ChatgptProductFieldType.Height]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatgptProductFieldType.Height]
    ].nullable(),
  [ChatgptProductFieldType.Weight]:
    AttributeValueSchema[
      ChatGptProductAttributeType[ChatgptProductFieldType.Weight]
    ].nullable(),
  [ChatgptProductFieldType.AgeGroup]:
    createSafeNullableEnumSchema(AgeGroupSchema),
};

export type ChatGptProductAttributeValueType = {
  [key in ChatgptProductFieldType]: (typeof ChatGptProductAttributeSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export function getChatGptProductAttributeValue<
  T extends ChatgptProductFieldType,
>(field: T, value: unknown): ChatGptProductAttributeValueType[T] | null {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return ChatGptProductAttributeSchema[field].parse(
    value,
  ) as ChatGptProductAttributeValueType[T];
}

export function getChatGptProductAttributeType(field: ChatgptProductFieldType) {
  return ChatGptProductAttributeType[field];
}

const ChatGptProductMappingSchema_ = z.object({
  fieldName: ChatGptProductFieldTypeSchema,
  attributeDefinitionId: attributeDefinitionIdFormat.schema,
});
export const ChatGptProductMappingSchema: z.ZodType<ChatGptProductMapping> =
  ChatGptProductMappingSchema_;
export interface ChatGptProductMapping extends z.infer<
  typeof ChatGptProductMappingSchema_
> {}

const ChatGptMappingSchema_ = z.object({
  productMappings: z.array(ChatGptProductMappingSchema).readonly(),
  variantMappings: z.array(ChatGptVariantMappingSchema).readonly(),
});
export const ChatGptMappingSchema: z.ZodType<ChatGptMapping> =
  ChatGptMappingSchema_;
export interface ChatGptMapping extends z.infer<typeof ChatGptMappingSchema_> {}

export function getChatGptListingAttributeValue<
  T extends ChatgptProductFieldType,
>({
  listing,
  field,
}: {
  listing: ChatGptListing;
  field: T;
}): AttributeValueType[AttributeType] | null {
  let fieldValue;
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (field) {
    case "title":
      fieldValue = listing.title;
      break;
    case "description":
      fieldValue = listing.description;
      break;
    case "material":
      fieldValue = listing.material;
      break;
    case "length":
      fieldValue = listing.length;
      break;
    case "width":
      fieldValue = listing.width;
      break;
    case "height":
      fieldValue = listing.height;
      break;
    case "weight":
      fieldValue = listing.weight;
      break;
    case "age_group":
      fieldValue = listing.ageGroup;
      break;
    case "product_category":
      fieldValue = listing.productCategory;
      break;
    case "condition":
      fieldValue = listing.condition;
      break;
    case "brand":
      fieldValue = listing.brand;
      break;
    case "link":
      fieldValue = listing.link;
      break;
    default:
      assertNever(field);
  }
  return ChatGptProductAttributeSchema[field].parse(fieldValue);
}
